<template>
  <div class="activity-stage steps-container venn-diagram-container" :class="{ isMobile }">
    <div class="scaled">
      <div class="venn-diagram-circles">
        <svg width="980px" height="460px" v-html="vennDiagramSVG"></svg>
      </div>
      <div class="left-paragraph-container step">
        <div class="venn-image-left">
          <img v-if="currentRound.imageLeft" :src="currentRound.imageLeft" />
        </div>
        <div :key="step.stepIndex" v-for="step in stepsLeft" class="left-paragraph">
          <blinking-cursor-text :text-step="step.stepIndex" :text="step.answer">
          </blinking-cursor-text>
        </div>
      </div>
      <div class="center-paragraph-container step">
        <div class="venn-image-center">
          <img v-if="currentRound.imageCenter" :src="currentRound.imageCenter" />
        </div>
        <div :key="step.stepIndex" v-for="step in stepsCenter" class="center-paragraph">
          <blinking-cursor-text :text-step="step.stepIndex" :text="step.answer">
          </blinking-cursor-text>
        </div>
      </div>
      <div class="right-paragraph-container step">
        <div class="venn-image-right">
          <img v-if="currentRound.imageRight" :src="currentRound.imageRight" />
        </div>
        <div :key="step.stepIndex" v-for="step in stepsRight" class="right-paragraph">
          <blinking-cursor-text :text-step="step.stepIndex" :text="step.answer">
          </blinking-cursor-text>
        </div>
      </div>
    </div>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';
import vennDiagramSVG from 'assets/svg/venn-diagram.svg';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import { createNamespacedHelpers } from 'vuex';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

const application = createNamespacedHelpers('application');

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    BlinkingCursorText
  },

  data() {
    return {
      completedStepsForPosition: 0,
      currentActive: 0,
      vennDiagramSVG
    };
  },
  computed: {
    ...application.mapState({
      isMobile: state => state.device.isMobile
    }),
    stepsLeft() {
      return this.filterByPosition('left');
    },
    stepsCenter() {
      return this.filterByPosition('center');
    },
    stepsRight() {
      return this.filterByPosition('right');
    }
  },
  methods: {
    filterByPosition(position) {
      if (!this.currentRound.steps) {
        return [];
      }
      return this.currentRound.steps.filter(s => s.position === position);
    },

    getActiveStep() {
      switch (this.currentActive) {
        case 0:
          return this.stepsLeft;
        case 1:
          return this.stepsRight;
        default:
          return this.stepsCenter;
      }
    },

    getCurrentDOM() {
      switch (this.currentActive) {
        case 0:
          return '.left-paragraph-container';
        case 1:
          return '.right-paragraph-container';
        default:
          return '.center-paragraph-container';
      }
    },

    setContainerHeight() {
      const keyboard = document.querySelector('.phoenix-keyboard-wrapper');
      const container = document.querySelector('.venn-diagram-container');
      container.style.height = `calc(100% - ${keyboard.offsetHeight + 20}px)`;
    },

    async loadActivity() {
      const activityState = this.getActivityState();
      await this.loadImages(activityState);
      this.createRounds(activityState);
    },

    async loadImages({ imageLeft, imageRight, imageCenter }) {
      this.queueImage([imageLeft, imageRight, imageCenter]);
      this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;
      return {
        rounds,
        instructions: {
          delay: 5,
          written: 'Type the words and phrases.',
          voice: `${this.voiceInstructionsPrefix}/venndiagram-${this.getRandomVoice()}-1.mp3`
        }
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
      this.$nextTick(() => {
        this.setContainerHeight();
        TweenMax.to(this.getCurrentDOM(), 1, { css: { opacity: 1 } });
      });
    },

    handleNewRound() {
      const self = this;

      TweenMax.to('.step', 0.5, {
        opacity: 0,
        onComplete() {
          self.setupNewRound();
          self.$nextTick(() => {
            self.currentActive = 0;
            self.completedStepsForPosition = 0;
            TweenMax.to(self.getCurrentDOM(), 1, { css: { opacity: 1 } });
          });
        }
      });
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        hideNumberRow: false,
        keyboardType,
        roofs
      };
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      const activeStep = this.getActiveStep(this.currentActive);

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();
        this.completedStepsForPosition += 1;

        if (this.completedStepsForPosition === activeStep.length) {
          this.currentActive += 1;
          this.completedStepsForPosition = 0;
          TweenMax.to(this.getCurrentDOM(), 1, { css: { opacity: 1 } });
        }

        if (this.currentActive < 3) return;
        if (this.isEndOfActivity) {
          this.playCongrats();
          TweenMax.delayedCall(1, () => this.completeActivity(false));
          return;
        }

        this.handleNewRound();
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.scaled {
  margin-top: -15px;
  transform: scale(0.8);
}

.left-paragraph {
  margin-left: -15px;
}

.center-paragraph {
  margin-left: 15px;
}

.left-paragraph-container {
  opacity: 0;
  position: absolute;
  top: 60px;
  left: 130px;
  width: 300px;
  height: 300px;
}

.center-paragraph-container {
  opacity: 0;
  position: absolute;
  top: 35%;
  left: 50%;
  transform: translateX(-50%);
}

.right-paragraph-container {
  opacity: 0;
  position: absolute;
  top: 60px;
  right: 60px;
  width: 300px;
  height: 300px;
}

.venn-diagram-container {
  width: 1024px;
  height: 480px;
  position: relative;

  .venn-diagram-circles {
    padding: 0 20px;
  }

  p {
    font-size: 22px;
  }

  .venn-image-left,
  .venn-image-right {
    width: 180px;
    height: 110px;
    position: relative;
    margin-bottom: 10px;
  }

  .venn-image-left {
    left: 60px;
  }

  .venn-image-right {
    left: -20px;
  }

  img {
    width: 180px;
  }
}

.venn-diagram-container.isMobile {
  p {
    font-size: 20px !important;
  }
}
</style>
